import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Upload, FileText, Trash2, Pencil, Save, Plus, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
const CHARACTER_INFO = {
  orb:        { name: 'ORB Ai Assistant', image: null, color: '#586D52' },
  mena:       { name: 'Mena ORB',    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/67lqvc7a_Mena%20Standard.png',    color: '#8B5CF6' },
  katie:      { name: 'Katie ORB',   image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/b90ayuat_katie%20standard.png',   color: '#EC4899' },
  luke:       { name: 'Luke ORB',    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/ue5pv5dv_Luke%20Standard.png',    color: '#3B82F6' },
  dorian:     { name: 'Dorian ORB',  image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/bi6kcptf_Dorian%20Standard.png',  color: '#F59E0B' },
  octavio:    { name: 'Octavio ORB', image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/zi3ibx7q_Octavio%20Standard.png', color: '#10B981' },
  summarizer: { name: 'Chat Summarizer (Internal)', image: null, color: '#0EA5E9', isInternal: true },
  user_summarizer: { name: 'User Summarizer (Internal)', image: null, color: '#7C3AED', isInternal: true },
  call_recorder: { name: 'Call Recorder', image: null, color: '#DC2626' },
};

const STRICTNESS_DESCRIPTIONS = {
  lax: 'Free to use trained data and general knowledge.',
  moderate: 'Trained data first, general knowledge only as fallback.',
  strict: 'Only the trained data may be used. No outside knowledge.',
};

const KIND_LABELS = {
  knowledge: 'Knowledge',
  persona: 'Persona',
  rules: 'Rules / Duties / Role',
};

export default function CharacterTrainingPage() {
  const { characterId } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const info = CHARACTER_INFO[characterId];
  const isOrb = characterId === 'orb';

  const [activeTab, setActiveTab] = useState('knowledge');
  const [entries, setEntries] = useState([]);
  const [inheritedOrb, setInheritedOrb] = useState([]);
  const [strictness, setStrictness] = useState('moderate');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [draft, setDraft] = useState({ title: '', content: '' });
  const [editEntry, setEditEntry] = useState(null);
  const [viewEntry, setViewEntry] = useState(null);
  const fileRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/training/${characterId}`);
      setEntries(res.data.entries || []);
      setInheritedOrb(res.data.inherited_orb_entries || []);
      setStrictness(res.data.settings?.strictness || 'moderate');
    } catch (e) {
      toast.error('Failed to load training data');
    }
    setLoading(false);
  }, [api, characterId]);

  useEffect(() => {
    if (!info) return;
    fetchAll();
  }, [info, fetchAll]);

  const isAdmin = user?.role === 'admin';
  const canAccess = isAdmin || (user?.feature_toggles || {}).admin_ai_training === true;
  const canEdit = isAdmin;

  if (!info || !canAccess) {
    return (
      <div className="container max-w-3xl mx-auto p-8 text-center">
        <p className="text-muted-foreground">Admin only or character not found.</p>
      </div>
    );
  }

  const filtered = entries.filter((e) => e.kind === activeTab);
  const filteredInherited = inheritedOrb.filter((e) => e.kind === activeTab);

  const handleAddText = async () => {
    if (!draft.content.trim()) return toast.error('Content is required');
    setSubmitting(true);
    try {
      await api.post(`/admin/training/${characterId}/text`, {
        title: draft.title,
        content: draft.content,
        kind: activeTab,
      });
      setDraft({ title: '', content: '' });
      toast.success(`${KIND_LABELS[activeTab]} added`);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add');
    }
    setSubmitting(false);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', activeTab);
    fd.append('title', '');
    setSubmitting(true);
    try {
      await api.post(`/admin/training/${characterId}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to upload');
    }
    setSubmitting(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await api.delete(`/admin/training/entry/${id}`);
      toast.success('Deleted');
      await fetchAll();
      if (viewEntry?.id === id) setViewEntry(null);
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleEditSave = async () => {
    if (!editEntry) return;
    try {
      await api.put(`/admin/training/entry/${editEntry.id}`, {
        title: editEntry.title,
        content: editEntry.content,
        kind: editEntry.kind,
      });
      toast.success('Saved');
      setEditEntry(null);
      await fetchAll();
    } catch (e) {
      toast.error('Save failed');
    }
  };

  const handleStrictnessChange = async (value) => {
    setStrictness(value);
    try {
      await api.put(`/admin/training/${characterId}/settings`, { strictness: value });
      toast.success(`Strictness set to ${value.toUpperCase()}`);
    } catch (e) {
      toast.error('Failed to save strictness');
    }
  };

  return (
    <div className="container max-w-4xl mx-auto px-4 py-6" data-testid="character-training-page">
      <button
        onClick={() => navigate('/admin/ai-training')}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        data-testid="training-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> All characters
      </button>

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div
          className="h-16 w-14 rounded-xl overflow-hidden border-2 flex items-center justify-center"
          style={{ borderColor: info.color, background: isOrb || info.isInternal ? info.color : 'transparent' }}
        >
          {isOrb ? (
            <Sparkles className="w-7 h-7 text-white" />
          ) : info.isInternal ? (
            <FileText className="w-7 h-7 text-white" />
          ) : (
            <img src={info.image} alt={info.name} className="w-full h-full object-cover object-top" style={{ aspectRatio: '3/4' }} />
          )}
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">{info.name} Training</h1>
          {isOrb && (
            <p className="text-xs text-muted-foreground mt-1">
              Anything added here is automatically applied to all 5 characters.
            </p>
          )}
          {info.isInternal && (
            <p className="text-xs text-muted-foreground mt-1">
              Internal persona used for all AI summaries (lyrics, stories, conversation summaries).
            </p>
          )}
        </div>
      </div>

      {!canEdit && (
        <div className="rounded-md bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-700 p-2 mb-3 text-[11px] text-amber-700 dark:text-amber-300">
          Read-only — AI agent training and toggles can only be changed by a full admin.
        </div>
      )}

      {/* Strictness */}
      <div className="rounded-xl border bg-card p-4 mb-6">
        <p className="text-sm font-bold mb-2">Response Strictness</p>
        <div className="grid grid-cols-3 gap-2 mb-2">
          {['lax', 'moderate', 'strict'].map((s) => (
            <button
              key={s}
              onClick={() => canEdit && handleStrictnessChange(s)}
              disabled={!canEdit}
              className={`px-3 py-2 rounded-lg text-sm font-semibold capitalize transition-colors ${
                strictness === s ? 'text-white' : 'bg-muted hover:bg-muted/80'
              } ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
              style={strictness === s ? { backgroundColor: info.color } : {}}
              data-testid={`strictness-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">{STRICTNESS_DESCRIPTIONS[strictness]}</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full mb-4">
          <TabsTrigger value="knowledge" data-testid="tab-knowledge">Knowledge</TabsTrigger>
          <TabsTrigger value="persona" data-testid="tab-persona">Persona</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">Rules</TabsTrigger>
        </TabsList>

        {['knowledge', 'persona', 'rules'].map((kind) => (
          <TabsContent key={kind} value={kind} className="space-y-4">
            {/* Add new */}
            <div className="rounded-xl border bg-card p-4 space-y-3">
              <p className="text-sm font-bold">Add {KIND_LABELS[kind]}</p>
              {kind === 'knowledge' && (
                <Input
                  placeholder="Title (optional)"
                  value={draft.title}
                  onChange={(e) => setDraft((p) => ({ ...p, title: e.target.value }))}
                  data-testid="training-title-input"
                />
              )}
              <textarea
                placeholder={
                  kind === 'persona'
                    ? 'Describe the persona this agent should embrace...'
                    : kind === 'rules'
                    ? 'Type rules, duties, must-do/must-not-do, general purpose...'
                    : 'Paste training content here, or upload a document below'
                }
                value={draft.content}
                onChange={(e) => setDraft((p) => ({ ...p, content: e.target.value }))}
                className="w-full min-h-[120px] rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': `${info.color}4D` }}
                data-testid="training-content-input"
              />
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={handleAddText}
                  disabled={submitting || !canEdit}
                  className="text-white hover:brightness-110"
                  style={{ backgroundColor: info.color }}
                  data-testid="training-add-text-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                  Add
                </Button>
                {kind === 'knowledge' && (
                  <>
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".pdf,.docx,.txt,.md"
                      hidden
                      onChange={(e) => handleUpload(e.target.files?.[0])}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileRef.current?.click()}
                      disabled={submitting || !canEdit}
                      data-testid="training-upload-btn"
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Upload (.pdf, .docx, .txt, .md)
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Existing entries */}
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-2">
                {filtered.length} entr{filtered.length === 1 ? 'y' : 'ies'} for {info.name}
              </p>
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
              ) : filtered.length === 0 ? (
                <p className="text-sm text-muted-foreground italic px-1">No entries yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filtered.map((e) => {
                    const isAuto = e.source === 'auto_profile';
                    return (
                      <button
                        key={e.id}
                        onClick={() => setViewEntry(e)}
                        className="text-left rounded-xl border p-3 transition-colors hover:opacity-90"
                        style={isAuto
                          ? { borderColor: `${info.color}66`, borderWidth: 2, backgroundColor: `${info.color}0D` }
                          : { borderColor: undefined }}
                        onMouseEnter={(ev) => { ev.currentTarget.style.borderColor = info.color; }}
                        onMouseLeave={(ev) => { ev.currentTarget.style.borderColor = isAuto ? `${info.color}66` : ''; }}
                        data-testid={`training-entry-${e.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          {isAuto ? (
                            <Sparkles className="w-4 h-4" style={{ color: info.color }} />
                          ) : e.source === 'document' ? (
                            <FileText className="w-4 h-4" style={{ color: info.color }} />
                          ) : (
                            <Pencil className="w-4 h-4 text-muted-foreground" />
                          )}
                          <p className="text-sm font-bold flex-1 truncate">{e.title || 'Untitled'}</p>
                          {isAuto && (
                            <span
                              className="text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded"
                              style={{ color: info.color, backgroundColor: `${info.color}26` }}
                            >
                              Auto
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-3">{e.content}</p>
                        <p className="text-[10px] text-muted-foreground mt-2">
                          {isAuto ? 'Auto-updated' : new Date(e.created_at).toLocaleDateString()}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Inherited from ORB Assistant */}
            {!isOrb && filteredInherited.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2 mt-4">
                  Inherited from ORB Ai Assistant ({filteredInherited.length})
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filteredInherited.map((e) => (
                    <div
                      key={e.id}
                      className="rounded-xl border border-dashed bg-muted/30 p-3"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="w-3.5 h-3.5" style={{ color: info.color }} />
                        <p className="text-xs font-bold flex-1 truncate">{e.title || 'Untitled'}</p>
                      </div>
                      <p className="text-[11px] text-muted-foreground line-clamp-2">{e.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* View Entry Dialog */}
      <Dialog open={!!viewEntry} onOpenChange={(o) => !o && setViewEntry(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{viewEntry?.title || 'Untitled'}</DialogTitle>
          </DialogHeader>
          {viewEntry && (
            <div className="space-y-3">
              <div className="flex gap-2 text-xs text-muted-foreground">
                <span>{KIND_LABELS[viewEntry.kind]}</span>
                <span>•</span>
                <span>{viewEntry.source === 'document' ? `File: ${viewEntry.filename}` : 'Text entry'}</span>
                <span>•</span>
                <span>{new Date(viewEntry.created_at).toLocaleString()}</span>
              </div>
              <div className="max-h-[50vh] overflow-y-auto rounded-lg border bg-muted/30 p-3 text-sm whitespace-pre-wrap">
                {viewEntry.content}
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setEditEntry({ ...viewEntry }); setViewEntry(null); }}
                  disabled={!canEdit}
                  data-testid="training-edit-btn"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(viewEntry.id)}
                  disabled={!canEdit}
                  data-testid="training-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={!!editEntry} onOpenChange={(o) => !o && setEditEntry(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Entry</DialogTitle>
          </DialogHeader>
          {editEntry && (
            <div className="space-y-3">
              <Input
                placeholder="Title"
                value={editEntry.title}
                onChange={(e) => setEditEntry((p) => ({ ...p, title: e.target.value }))}
              />
              <textarea
                value={editEntry.content}
                onChange={(e) => setEditEntry((p) => ({ ...p, content: e.target.value }))}
                className="w-full min-h-[260px] rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': `${info.color}4D` }}
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setEditEntry(null)}>Cancel</Button>
                <Button
                  onClick={handleEditSave}
                  disabled={!canEdit}
                  className="text-white hover:brightness-110"
                  style={{ backgroundColor: info.color }}
                  data-testid="training-edit-save"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
